import React, { useCallback, useEffect, useRef, useState } from "react";
import { registerLocale } from "react-datepicker";
import { tr } from "date-fns/locale";
import { ApiService } from "../services/http.service";
import { format } from "date-fns";
import { isMobile } from "../App";
import camera from "../assets/icons/camera.png";
import play from "../assets/icons/play-button.png";

import { dateForRequest, formatMilliseconds, handleOpenDetails, useForceUpdate } from "../services/misc.functions";
import DistanceSelect from "../components/shared/distance_select/distance_select";
import TrackSelect from "../components/shared/track_select/tract_select";
import WayType from "../components/shared/way_type/way_type";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import {
  dailyBulletin,
  results,
  shownRun,
  shownRunSubject,
  statisticsOptions,
  statisticsOptionsSubject,
} from "../stores/statistics.store";
import _ from "lodash";
import { modalServiceSubject } from "../components/shared/modal/Modal";

registerLocale("tr", tr);

export const PreviousRuns: React.FC<any> = () => {
  const [runData, setRunData] = useState<any>(null);
  const [distances, setDistances] = useState<string[]>([]);
  const [selectedDistance, setSelectedDistance] = useState<string | null>(null);
  const [selectedTracks, setSelectedTracks] = useState<{ label: string; value: string }[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const forceUpdate = useForceUpdate();

  const previousDistance = useRef<any>(null);

  const fetchData = useCallback(
    (date: Date) => {
      if (previousDistance.current === null && selectedDistance !== null) {
        previousDistance.current = selectedDistance;
        return;
      }

      setLoading(true);
      const api = new ApiService();

      api
        .start("post", "https://tjk-statistics.bitrelic.io/api/previous-runs/", {
          date: dateForRequest(date),
          distance: selectedDistance,
        })
        .then((response: any) => {
          results.length = 0;
          results.push(...response.data);

          const selectedRun = response.data.find(
            (item: any) => item.hippodrome_key === shownRun.hippodrome_key && item.run_no === shownRun.run_no
          );

          if (selectedRun) {
            setRunData(selectedRun);
            setDistances(selectedRun.distance_options);

            if (!selectedDistance) {
              setSelectedDistance(selectedRun.distance);
            }
          }
        })
        .catch((error: any) => {
          console.error("Failed to fetch data", error);
        })
        .finally(() => {
          setLoading(false);
          forceUpdate();
        });
    },
    [selectedDistance, shownRun]
  );

  useEffect(() => {
    const statisticsSub = statisticsOptionsSubject.subscribe(() => {
      fetchData(statisticsOptions.date);
    });

    const shownRunSub = shownRunSubject.subscribe(() => {
      const selectedRun = results.find(
        (item: any) => item.hippodrome_key === shownRun.hippodrome_key && item.run_no === shownRun.run_no
      );
      if (selectedRun) {
        setRunData(selectedRun);
        setSelectedDistance(selectedRun.distance);
      }
      forceUpdate();
    });

    fetchData(statisticsOptions.date);

    return () => {
      statisticsSub.unsubscribe();
      shownRunSub.unsubscribe();
    };
  }, [fetchData, statisticsOptions.date]);

  const handleTrackChange = (selectedOptions: any) => {
    setSelectedTracks(selectedOptions);
  };

  const handleDistanceChange = (distance: string) => {
    setSelectedDistance(distance);
  };

  const [visibleRunners, setVisibleRunners] = useState<{ [key: string]: boolean }>({});
  const toggleRunnersVisibility = (horseName: string) => {
    setVisibleRunners((prev) => ({
      ...prev,
      [horseName]: !prev[horseName],
    }));
  };

  return (
    <div className="pt-0">
      <div>
        {!isMobile ? (
          <div className="statistics-filters">
            <div className="col-lg-8">
              <DistanceSelect
                distances={distances}
                activeDistance={selectedDistance}
                changeActiveDistance={handleDistanceChange}
              />
            </div>

            <div className="col-lg-4">
              <TrackSelect activeTracks={selectedTracks} setActiveTracks={handleTrackChange} />
            </div>
          </div>
        ) : (
          <div className="statistics-filters-mobile">
            <div className="col-5">
              <DistanceSelect
                distances={distances}
                activeDistance={selectedDistance}
                changeActiveDistance={handleDistanceChange}
              />
            </div>

            <div className="col-7">
              <TrackSelect activeTracks={selectedTracks} setActiveTracks={handleTrackChange} />
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center p-1">
            <div id="id_loaderAnimation" className="loader"></div>
          </div>
        ) : (
          <>
            {!isMobile ? (
              <div className="table-responsive m-2">
                <table className="table table-borderless">
                  {_.sortBy(runData.runners_data, "horse_no").map((runner: any, keyHorse: number) => {
                    const filteredDegrees = runner.best_degree.filter((degree: any) => {
                      // const matchesDistance = selectedDistance ? degree.distance === selectedDistance : true;
                      const matchesTrack = selectedTracks.length
                        ? selectedTracks.some((track) => track.label === degree.running_track_name)
                        : true;
                      return matchesTrack;
                    });

                    const currentDetail = dailyBulletin
                      .find((item: any) => item.keyword === statisticsOptions.selectedHippodrome.tjk_key)
                      ?.runs?.find((run: any) => run.no == shownRun.run_no)
                      ?.horses?.find((horse: any) => horse.no == runner.horse_no);

                    return (
                      <tbody key={`DataTable-runners-${keyHorse}`}>
                        <tr style={{ cursor: "pointer", backgroundColor: "#9CA0E9" }}>
                          <td
                            colSpan={3}
                            style={{
                              color: "#fff",
                              fontWeight: "bold",
                              borderTopLeftRadius: "5px",
                            }}
                          >
                            {runner.horse_no} - {runner.horse_name}
                          </td>

                          <td
                            colSpan={4}
                            style={{
                              color: "#253E9E",
                              fontWeight: "bold",
                              borderTopRightRadius: "5px",
                              backgroundColor: "#D7D9F7",
                            }}
                          >
                            <div className="row">
                              <div className="col-4 text-center">{currentDetail?.jokey.short_name}</div>
                              <div className="col-4 text-center">{currentDetail?.weight} KG</div>
                              <div className="col-4 text-center">ST {currentDetail?.start}</div>
                            </div>
                          </td>
                        </tr>

                        <tr style={{ backgroundColor: "#EDEEFB" }}>
                          <td>Tarih</td>
                          <td>Şehir</td>
                          <td>Mesafe</td>
                          <td>Pist</td>
                          <td>Sıra</td>
                          <td>Kilo</td>
                          <td>Jokey</td>
                          <td>Start</td>
                          <td>GNY</td>
                          <td>Grup</td>
                          <td>Şart</td>
                          <td>Derece</td>
                          <td>HP</td>
                          <td>AGF</td>
                          <td className="text-center">FF</td>
                          {/* <td className="text-center" style={{ borderTopRightRadius: "5px" }}>
                            Video
                          </td> */}
                        </tr>

                        {filteredDegrees.length > 0 ? (
                          filteredDegrees.map((degree: any, index: number) => {
                            return (
                              <React.Fragment key={`DataTable-degrees-${keyHorse}-${index}`}>
                                <tr
                                  className="elevated-row"
                                  style={{ cursor: "pointer" }}
                                  onClick={() => handleOpenDetails(degree.date, degree.location, shownRun.run_no)}
                                >
                                  <td>{format(new Date(degree.date), "dd.MM.yyyy")}</td>
                                  <td>{degree.location}</td>
                                  <td>{degree.distance}</td>
                                  <td>{degree.running_track_name}</td>
                                  <td>
                                    {degree.result || degree.start_no}/{degree.runner_count}
                                  </td>
                                  <td>{degree.weight}</td>
                                  <td>{degree.jockey.name}</td>
                                  <td>{degree.start_no}</td>
                                  <td>{degree.ganyan}</td>
                                  <td>{degree.run_group_short}</td>
                                  <td>{degree.run_breed_detail}</td>
                                  <td>{formatMilliseconds(degree.degree)}</td>
                                  <td>{degree.handicap}</td>
                                  <td>%{degree.agf_1}</td>
                                  <td className="text-center" onClick={(event) => event.stopPropagation()}>
                                    {degree.photo_finish ? (
                                      <img
                                        src={camera}
                                        alt="FF"
                                        width={15}
                                        height={15}
                                        className="mb-1"
                                        onClick={() => {
                                          modalServiceSubject.next({
                                            content: (
                                              <img
                                                alt="photo_finish"
                                                src={decodeURI(degree.photo_finish)}
                                                style={{
                                                  maxWidth: "100%",
                                                  height: "auto",
                                                  width: "auto",
                                                }}
                                              />
                                            ),
                                            confirm: {
                                              cancel: {
                                                label: "Kapat",
                                              },
                                            },
                                          });
                                        }}
                                      />
                                    ) : (
                                      ""
                                    )}
                                  </td>
                                  {/* <td className="text-center" onClick={(event) => event.stopPropagation()}>
                                    {degree.video ? (
                                      <img
                                        src={play}
                                        alt="play"
                                        width={15}
                                        height={15}
                                        className="mb-1"
                                        onClick={() => {
                                          modalServiceSubject.next({
                                            content: (
                                              <video controls autoPlay style={{ width: "100%" }}>
                                                <source src={decodeURI(degree.video)} type="video/mp4" />
                                              </video>
                                            ),

                                            confirm: {
                                              cancel: {
                                                label: "Kapat",
                                              },
                                            },
                                          });
                                        }}
                                      />
                                    ) : (
                                      ""
                                    )}
                                  </td> */}
                                </tr>
                              </React.Fragment>
                            );
                          })
                        ) : (
                          <tr className="elevated-row">
                            <td colSpan={14} className="text-center">
                              Bu kriterlerde koşusu bulunmamaktadır.
                            </td>
                          </tr>
                        )}

                        <tr>
                          <th className="seperator"></th>
                        </tr>
                        <tr>
                          <th className="seperator"></th>
                        </tr>
                      </tbody>
                    );
                  })}
                </table>
              </div>
            ) : (
              <div className="mobile-best-degree-table">
                {_.sortBy(runData.runners_data, "horse_no").map((runner: any, keyHorse: number) => {
                  const filteredDegrees = runner.best_degree.filter((degree: any) => {
                    const matchesTrack = selectedTracks.length
                      ? selectedTracks.some((track) => track.label === degree.running_track_name)
                      : true;
                    return matchesTrack;
                  });
                  const isVisible = visibleRunners[runner.horse_name] || false;
                  return (
                    <div className="table-item" key={`DataTable-horse-${keyHorse}`}>
                      <div
                        className="row"
                        key={`best-degree-${keyHorse}`}
                        onClick={() => toggleRunnersVisibility(runner.horse_name)}
                      >
                        <div className="col-1 horse-no">{runner.horse_no || "-"}</div>

                        <div className="col-10 horse-info justify-content-center" style={{ maxWidth: "300px" }}>
                          <span className="fw-bold horse-name ms-1" style={{ maxWidth: "300px" }}>
                            {runner.horse_name || "-"}
                          </span>
                        </div>

                        <div className="col-1 d-flex align-items-center justify-content-end">
                          {filteredDegrees.length > 0 ? (
                            isVisible ? (
                              <IoIosArrowUp />
                            ) : (
                              <IoIosArrowDown />
                            )
                          ) : (
                            <span className="text-nowrap" style={{ fontSize: "10px" }}>
                              {" "}
                              Bu kriterlerde koşusu bulunmamaktadır.
                            </span>
                          )}
                        </div>
                      </div>
                      {filteredDegrees.length > 0 &&
                        filteredDegrees.map((degree: any, degreeIndex: number) => (
                          <div
                            className={`row flex-nowrap ${isVisible ? "show" : "hide"} mt-2`}
                            key={`best-degree-${degreeIndex}`}
                            onClick={() => handleOpenDetails(degree.date, degree.location, shownRun.run_no)}
                          >
                            <div className="col-5 horse-info">
                              <span style={{ lineHeight: "18px" }}>
                                {format(new Date(degree.date), "dd.MM.yyyy")} /{" "}
                                <span className="fw-bold">{degree.location.split(" ")[0]}</span>
                                <span> / {degree.run_group_short}</span>
                              </span>
                              <div className="d-flex flex-row text-nowrap flex-nowrap">
                                {degree.distance}m /
                                <div
                                  style={{
                                    width: "20px",
                                    height: "10px",
                                    marginInline: "1px",
                                    marginTop: "2px",
                                  }}
                                >
                                  <WayType type={degree.running_track_name} />
                                </div>
                                / {degree.run_breed_detail}
                              </div>
                            </div>

                            <div className="col-4 runner-info">
                              <span className="fw-bold runner-name">{degree.jockey.name}</span>
                              <span>
                                {degree.weight}kg / HP: {degree.handicap}
                              </span>
                              <span>
                                GNY: {degree.ganyan} / AGF: %{degree.agf_1}
                              </span>
                            </div>

                            <div className="col-2 runner-result">
                              <span className="degree">{formatMilliseconds(degree.degree)}</span>
                              <span>
                                S / <span className="fw-bold">{degree.start_no}</span>
                              </span>
                              <span>
                                F /{" "}
                                <span className="fw-bold">
                                  {degree.result || degree.start_no}/{degree.runner_count}
                                </span>
                              </span>
                            </div>

                            <div
                              className="col-1 runner-media"
                              onClick={(event) => {
                                event.stopPropagation();
                              }}
                            >
                              {degree.photo_finish && decodeURI(degree.photo_finish).trim() ? (
                                <img
                                  src={camera}
                                  alt="FF"
                                  width={15}
                                  height={15}
                                  className="mb-1"
                                  onClick={() => {
                                    modalServiceSubject.next({
                                      content: (
                                        <img
                                          alt="photo_finish"
                                          src={decodeURI(degree.photo_finish)}
                                          style={{
                                            maxWidth: "100%",
                                            height: "auto",
                                            width: "auto",
                                          }}
                                        />
                                      ),
                                      confirm: {
                                        cancel: {
                                          label: "Kapat",
                                        },
                                      },
                                    });
                                  }}
                                />
                              ) : (
                                <span>-</span>
                              )}

                              {/* {degree.video && decodeURI(degree.video).trim() ? (
                                <img
                                  src={play}
                                  alt="play"
                                  width={15}
                                  height={15}
                                  className="mb-1"
                                  onClick={() => {
                                    modalServiceSubject.next({
                                      content: (
                                        <video controls autoPlay style={{ width: "100%" }}>
                                          <source src={decodeURI(degree.video)} type="video/mp4" />
                                        </video>
                                      ),

                                      confirm: {
                                        cancel: {
                                          label: "Kapat",
                                        },
                                      },
                                    });
                                  }}
                                />
                              ) : (
                                <span>-</span>
                              )} */}
                            </div>
                          </div>
                        ))}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      <div className="info">
        <span className="title">Son Koşular</span>
        <span className="description">Atların, katıldıkları son resmi yarışlarını ifade eder.</span>
      </div>
    </div>
  );
};
